import React, { useState, useEffect } from 'react';
import { Menu, X, Zap } from 'lucide-react';
import ProductsPage from './ProductsPage';
import TradingViewTickerTape from './TradingViewTickerTape';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll(); // Call once on mount
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Empty deps is correct - we want this to set up once

  const navItems = [
    { name: 'Home', href: '#home', type: 'scroll' },
    { name: 'Products', href: '#products', type: 'page' },
    { name: 'Performance', href: '#performance', type: 'scroll' },
    { name: 'Profit Simulator', href: '#profit-simulator', type: 'scroll' },
    { name: 'Reviews', href: '#reviews', type: 'scroll' },
    { name: 'About', href: '#about', type: 'scroll' },
    { name: 'Blog', href: '#blog', type: 'scroll' },
    { name: 'Market News', href: '#market-news', type: 'scroll' },
    { name: 'Contact Us', href: '#contact', type: 'scroll' }
  ];

  const handleNavClick = (e, item) => {
    e.preventDefault();
    
    if (item.type === 'page' && item.name === 'Products') {
      setIsProductsOpen(true);
      setIsMenuOpen(false);
    } else {
      scrollToSection(e, item.href);
    }
  };

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const targetId = href.replace('#', '');
    const element = document.getElementById(targetId);
    
    if (element) {
      // Increased offset for mobile to account for header + ticker tape
      const offset = window.innerWidth < 768 ? 140 : 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false);
  };

  return (
    <>
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-black/95 backdrop-blur-xl border-b border-emerald-500/20 shadow-lg shadow-emerald-500/5' 
        : 'bg-slate-950/80 backdrop-blur-md border-b border-slate-800/50'
    }`}>
      <div className="w-full px-3 sm:px-4 lg:px-8 max-w-full">
        <div className={`flex items-center justify-between transition-all duration-300 ${
          isScrolled ? 'h-16' : 'h-18'
        }`}>
          {/* Logo */}
          <a href="#home" onClick={(e) => scrollToSection(e, '#home')} className="flex items-center gap-2 sm:gap-3 group flex-shrink-0">
            <img 
              src="/logo.png" 
              alt="Marketwise Solutions Logo" 
              className={`object-contain group-hover:scale-110 transition-all duration-300 ${
                isScrolled ? 'h-10 w-10 sm:h-12 sm:w-12' : 'h-12 w-12 sm:h-14 sm:w-14'
              }`}
            />
            <div className="text-white font-bold whitespace-nowrap">
              <span className="text-sm sm:text-base lg:text-lg">Marketwise Solutions</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => handleNavClick(e, item)}
                className="text-slate-300 hover:text-emerald-400 font-medium transition-colors duration-200 cursor-pointer text-sm relative group"
              >
                {item.name}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-400 to-cyan-400 group-hover:w-full transition-all duration-300"></span>
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:block">
            <a 
              href="#pricing"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className={`inline-flex items-center gap-2 px-6 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-black font-bold rounded-lg transition-all duration-300 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 ${
                isScrolled ? 'py-2.5 text-sm' : 'py-3 text-base'
              }`}
            >
              <Zap className="w-4 h-4" />
              Get Started
            </a>
          </div>

          {/* Mobile Menu Button & WhatsApp */}
          <div className="lg:hidden flex items-center gap-2 flex-shrink-0">
            <a
              href="https://wa.me/971554276609"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 hover:bg-emerald-500/10 rounded-lg transition-colors"
              aria-label="WhatsApp"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </a>
            <button
              className="text-white p-2 hover:bg-emerald-500/10 rounded-lg transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Menu"
            >
              {isMenuOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-800/50 bg-black/95 backdrop-blur-xl">
            <nav className="flex flex-col gap-2">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item)}
                  className="text-slate-300 hover:text-emerald-400 font-medium transition-colors duration-200 cursor-pointer py-2.5 px-3 hover:bg-emerald-500/5 rounded-lg text-sm"
                >
                  {item.name}
                </a>
              ))}
              <a 
                href="#pricing"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                  setIsMenuOpen(false);
                }}
                className="mt-2 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-black font-bold rounded-lg text-center flex items-center justify-center gap-2 text-sm"
              >
                <Zap className="w-4 h-4" />
                Get Started
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>

    {/* TradingView Ticker Tape - Below Header */}
    <div className={`fixed z-40 left-0 right-0 bg-black/95 backdrop-blur-xl border-b border-slate-800/50 overflow-hidden ${
      isScrolled ? 'top-16' : 'top-18'
    } transition-all duration-300`}>
      <TradingViewTickerTape />
    </div>
    
    <ProductsPage isOpen={isProductsOpen} onClose={() => setIsProductsOpen(false)} />
    </>
  );
};

export default Header;
